import { useState, useEffect, useRef, useCallback } from "react";
import HelpMe from "./HelpMe";

const P = {
  cream: "#F6F1E9", warm: "#EDE5D4", slate: "#252D3D", slateLight: "#3A4460",
  accent: "#C8773A", accentLight: "#F0A070", green: "#2E7D57", greenBg: "#E8F5EE",
  red: "#B03A3A", redBg: "#FAEBEB", muted: "#8A90A8", border: "#D8CFBE",
  white: "#FFFFFF", gold: "#C8A43A", amber: "#E8A020", amberBg: "#FEF3E2",
  saveGreen: "#1A7A3A",
};

const CATEGORIES = [
  { id: "housing", label: "Housing", icon: "🏠", color: "#7B6FA0", items: [
    { name: "Rent / Mortgage", suggested: 1200 }, { name: "Renter's Insurance", suggested: 18 },
    { name: "HOA Fees", suggested: 150 }, { name: "Storage Unit", suggested: 80 },
    { name: "Lawn / Yard Care", suggested: 60 }, { name: "Home Maintenance", suggested: 100 },
  ]},
  { id: "utilities", label: "Utilities", icon: "⚡", color: "#C8773A", items: [
    { name: "Electricity", suggested: 120 }, { name: "Gas / Heating", suggested: 60 },
    { name: "Water & Sewer", suggested: 50 }, { name: "Internet", suggested: 70 },
    { name: "Cell Phone", suggested: 80 }, { name: "Trash Pickup", suggested: 30 },
  ]},
  { id: "food", label: "Food & Groceries", icon: "🛒", color: "#5A8A5A", items: [
    { name: "Groceries", suggested: 400 }, { name: "Dining Out", suggested: 200 },
    { name: "Coffee / Drinks", suggested: 60 }, { name: "Meal Delivery Apps", suggested: 80 },
    { name: "Work Lunches", suggested: 100 },
  ]},
  { id: "transport", label: "Transportation", icon: "🚗", color: "#3A7A9A", items: [
    { name: "Car Payment", suggested: 400 }, { name: "Car Insurance", suggested: 130 },
    { name: "Gas / Fuel", suggested: 120 }, { name: "Car Maintenance", suggested: 60 },
    { name: "Parking / Tolls", suggested: 40 }, { name: "Public Transit", suggested: 80 },
    { name: "Rideshare (Uber/Lyft)", suggested: 50 },
  ]},
  { id: "insurance", label: "Insurance & Health", icon: "🏥", color: "#A05A5A", items: [
    { name: "Health Insurance Premium", suggested: 250 }, { name: "Dental Insurance", suggested: 30 },
    { name: "Vision Insurance", suggested: 15 }, { name: "Life Insurance", suggested: 25 },
    { name: "Prescriptions / Copays", suggested: 30 }, { name: "Gym / Fitness", suggested: 40 },
  ]},
  { id: "subscriptions", label: "Subscriptions", icon: "📱", color: "#6A5AAA", items: [
    { name: "Netflix", suggested: 18 }, { name: "Spotify / Music", suggested: 11 },
    { name: "Hulu / Disney+", suggested: 16 }, { name: "Amazon Prime", suggested: 15 },
    { name: "iCloud / Google Storage", suggested: 3 }, { name: "Gaming (Xbox / PS+)", suggested: 15 },
    { name: "News / Magazines", suggested: 12 }, { name: "Software / Apps", suggested: 20 },
  ]},
  { id: "personal", label: "Personal Care", icon: "✨", color: "#A07840", items: [
    { name: "Haircuts / Salon", suggested: 50 }, { name: "Clothing / Shoes", suggested: 100 },
    { name: "Toiletries / Cosmetics", suggested: 50 }, { name: "Laundry / Dry Cleaning", suggested: 20 },
    { name: "Pet Care / Vet", suggested: 80 },
  ]},
  { id: "entertainment", label: "Entertainment", icon: "🎉", color: "#8A6040", items: [
    { name: "Movies / Events", suggested: 60 }, { name: "Hobbies / Activities", suggested: 80 },
    { name: "Gifts", suggested: 50 }, { name: "Vacations (monthly set-aside)", suggested: 100 },
    { name: "Books / Education", suggested: 30 },
  ]},
];

const SAVINGS_GOALS = [
  { name: "Emergency Fund", suggested: 200, icon: "🛡️" },
  { name: "Vacation Fund", suggested: 100, icon: "✈️" },
  { name: "New Vehicle", suggested: 200, icon: "🚘" },
  { name: "Home Down Payment", suggested: 300, icon: "🏡" },
  { name: "Birthday / Holiday Gifts", suggested: 50, icon: "🎁" },
  { name: "New Computer", suggested: 50, icon: "💻" },
  { name: "New Phone", suggested: 40, icon: "📱" },
];

const DEBT_ITEMS = [
  { name: "Credit Card Payoff", icon: "💳" },
  { name: "Student Loan", icon: "🎓" },
  { name: "Medical Bills", icon: "🏥" },
  { name: "Personal Loan", icon: "🤝" },
  { name: "Car Loan Payoff", icon: "🚗" },
];

const PIN_TIMEOUTS = [
  { label: "1 minute", value: 1 },
  { label: "5 minutes", value: 5 },
  { label: "15 minutes", value: 15 },
  { label: "30 minutes", value: 30 },
  { label: "Only on fresh open", value: 9999 },
];

const MISC_CAT = { id: "misc", label: "Miscellaneous", icon: "📦", color: "#8A90A8" };
const ALL_CATS = [...CATEGORIES, MISC_CAT];

function fmt(n) { return (parseFloat(n)||0).toLocaleString("en-US",{style:"currency",currency:"USD",maximumFractionDigits:0}); }
function fmtFull(n) { return (parseFloat(n)||0).toLocaleString("en-US",{style:"currency",currency:"USD",minimumFractionDigits:2,maximumFractionDigits:2}); }
function load() { try { const s=localStorage.getItem("groundwork_v3"); return s?JSON.parse(s):null; } catch { return null; } }
function save(s) { try { localStorage.setItem("groundwork_v3",JSON.stringify(s)); } catch {} }

export default function App() {
  const saved = load();

  // ── Core state ──
  const [screen, setScreen] = useState("boot");
  const [name, setName] = useState(saved?.name||"");
  const [avatar, setAvatar] = useState(saved?.avatar||null);
  const [income, setIncome] = useState(saved?.income||"");
  const [incomeFreq, setIncomeFreq] = useState(saved?.incomeFreq||1);
  const [lineItems, setLineItems] = useState(saved?.lineItems||[]);
  const [savingsGoals, setSavingsGoals] = useState(saved?.savingsGoals||[]);
  const [debtItems, setDebtItems] = useState(saved?.debtItems||[]);
  const [showHelp, setShowHelp] = useState(false);

  // ── PIN state ──
  const [pinEnabled, setPinEnabled] = useState(saved?.pinEnabled||false);
  const [pinHash, setPinHash] = useState(saved?.pinHash||"");
  const [pinTimeout, setPinTimeout] = useState(saved?.pinTimeout||5);
  const [pinInput, setPinInput] = useState("");
  const [pinSetup, setPinSetup] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [pinError, setPinError] = useState("");
  const [pinStep, setPinStep] = useState("enter"); // enter | setup | confirm
  const [lastActive, setLastActive] = useState(Date.now());

  // ── UI state ──
  const [activeCategory, setActiveCategory] = useState(null);
  const [view, setView] = useState("categories");
  const [customName, setCustomName] = useState("");
  const [customAmount, setCustomAmount] = useState("");
  const [customFreq, setCustomFreq] = useState(1);
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [collapsedCats, setCollapsedCats] = useState({});
  const [customGoalName, setCustomGoalName] = useState("");
  const [customGoalTarget, setCustomGoalTarget] = useState("");
  const [customGoalMonthly, setCustomGoalMonthly] = useState("");
  const [customDebtName, setCustomDebtName] = useState("");
  const [customDebtBalance, setCustomDebtBalance] = useState("");
  const [customDebtMonthly, setCustomDebtMonthly] = useState("");
  const [toast, setToast] = useState(null);
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const [movingItem, setMovingItem] = useState(null);
  const [showPinSettings, setShowPinSettings] = useState(false);
  const fileRef = useRef();
  const importRef = useRef();

  // ── Boot: check PIN ──
  useEffect(() => {
    if (!saved) { setScreen("onboard"); return; }
    if (saved.pinEnabled && saved.pinHash) {
      const elapsed = (Date.now() - (saved.lastActive||0)) / 60000;
      if (elapsed > (saved.pinTimeout||5)) { setScreen("pin"); return; }
    }
    setScreen("main");
  }, []);

  // ── Track last active ──
  useEffect(() => {
    const tick = () => {
      setLastActive(Date.now());
      const s = load();
      if (s) save({ ...s, lastActive: Date.now() });
    };
    window.addEventListener("focus", tick);
    return () => window.removeEventListener("focus", tick);
  }, []);

  const persist = useCallback((patch) => {
    const next = { name, avatar, income, incomeFreq, lineItems, savingsGoals, debtItems, pinEnabled, pinHash, pinTimeout, lastActive, ...patch };
    save(next);
  }, [name, avatar, income, incomeFreq, lineItems, savingsGoals, debtItems, pinEnabled, pinHash, pinTimeout, lastActive]);

  useEffect(() => { if (screen === "main") persist({}); }, [persist, screen]);

  // ── Computed ──
  const monthlyIncome = parseFloat(income||0) * parseFloat(incomeFreq||1);
  const totalExpenses = lineItems.reduce((s,i) => s+(parseFloat(i.amount)||0)*i.freq, 0);
  const totalSavings = savingsGoals.reduce((s,g) => s+(parseFloat(g.monthly)||0), 0);
  const totalDebt = debtItems.reduce((s,d) => s+(parseFloat(d.monthly)||0), 0);
  const totalOut = totalExpenses + totalSavings + totalDebt;
  const delta = monthlyIncome - totalOut;
  const inGreen = delta >= 0;

  // ── Toast ──
  function showToast(msg, type="success") {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  }

  // ── Avatar ──
  function handleAvatar(e) {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setAvatar(ev.target.result); persist({ avatar: ev.target.result }); };
    reader.readAsDataURL(file);
  }

  // ── Items ──
  function addItem(catId, itemName, amount, freq=1) {
    const next = [...lineItems, { id: Date.now()+Math.random(), catId, name: itemName, amount: parseFloat(amount)||0, freq }];
    setLineItems(next); persist({ lineItems: next }); setHasUnsaved(true);
  }
  function removeItem(id) { const next=lineItems.filter(i=>i.id!==id); setLineItems(next); persist({lineItems:next}); setHasUnsaved(true); }
  function updateItem(id,val) { const next=lineItems.map(i=>i.id===id?{...i,amount:parseFloat(val)||0}:i); setLineItems(next); persist({lineItems:next}); setHasUnsaved(true); }
  function moveItem(id, newCatId) { const next=lineItems.map(i=>i.id===id?{...i,catId:newCatId}:i); setLineItems(next); persist({lineItems:next}); setHasUnsaved(true); setMovingItem(null); showToast("Expense moved! ✅"); }
  function isAdded(catId, itemName) { return lineItems.some(i=>i.catId===catId&&i.name===itemName); }

  // ── Savings ──
  function addSavingsGoal(goalName, target, monthly) { const next=[...savingsGoals,{id:Date.now()+Math.random(),name:goalName,target:parseFloat(target)||0,monthly:parseFloat(monthly)||0}]; setSavingsGoals(next); persist({savingsGoals:next}); setHasUnsaved(true); }
  function removeSavingsGoal(id) { const next=savingsGoals.filter(g=>g.id!==id); setSavingsGoals(next); persist({savingsGoals:next}); setHasUnsaved(true); }
  function updateSavingsGoal(id,field,val) { const next=savingsGoals.map(g=>g.id===id?{...g,[field]:parseFloat(val)||0}:g); setSavingsGoals(next); persist({savingsGoals:next}); setHasUnsaved(true); }
  function hasSavingsGoal(goalName) { return savingsGoals.some(g=>g.name===goalName); }

  // ── Debt ──
  function addDebtItem(debtName,balance,monthly) { const next=[...debtItems,{id:Date.now()+Math.random(),name:debtName,balance:parseFloat(balance)||0,monthly:parseFloat(monthly)||0}]; setDebtItems(next); persist({debtItems:next}); setHasUnsaved(true); }
  function removeDebtItem(id) { const next=debtItems.filter(d=>d.id!==id); setDebtItems(next); persist({debtItems:next}); setHasUnsaved(true); }
  function updateDebtItem(id,field,val) { const next=debtItems.map(d=>d.id===id?{...d,[field]:parseFloat(val)||0}:d); setDebtItems(next); persist({debtItems:next}); setHasUnsaved(true); }
  function hasDebtItem(debtName) { return debtItems.some(d=>d.name===debtName); }

  function toggleCollapse(id) { setCollapsedCats(prev=>({...prev,[id]:!prev[id]})); }

  function goBack() {
    setActiveCategory(null);
    setCustomName(""); setCustomAmount(""); setCustomFreq(1);
    setCustomGoalName(""); setCustomGoalTarget(""); setCustomGoalMonthly("");
    setCustomDebtName(""); setCustomDebtBalance(""); setCustomDebtMonthly("");
  }

  function handleOnboard() { if (!name.trim()) return; persist({name:name.trim(),income,incomeFreq}); setScreen("main"); }

  // ── Save / Export ──
  function handleSave() {
    persist({});
    showToast("Budget saved! 💾");
    setHasUnsaved(false);
  }

  function exportBudget() {
    const data = { name, avatar, income, incomeFreq, lineItems, savingsGoals, debtItems, exportedAt: new Date().toISOString() };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `groundwork-budget-${name.replace(/\s+/g,"-")}.json`; a.click();
    URL.revokeObjectURL(url);
    showToast("Exported! Check Downloads or Files app 📁");
    showToast("Saved! Check Downloads or Files app! 📁");
  }

  function importBudget(e) {
    const file = e.target.files[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        setName(data.name||""); setAvatar(data.avatar||null);
        setIncome(data.income||""); setIncomeFreq(data.incomeFreq||1);
        setLineItems(data.lineItems||[]); setSavingsGoals(data.savingsGoals||[]);
        setDebtItems(data.debtItems||[]);
        persist({ name:data.name, avatar:data.avatar, income:data.income, incomeFreq:data.incomeFreq, lineItems:data.lineItems||[], savingsGoals:data.savingsGoals||[], debtItems:data.debtItems||[] });
        showToast("Budget restored! ✅");
        setHasUnsaved(false);
      } catch { showToast("Couldn't read that file 😕", "error"); }
    };
    reader.readAsText(file);
  }

  // ── PIN ──
  function simpleHash(s) { let h=0; for(let i=0;i<s.length;i++){h=((h<<5)-h)+s.charCodeAt(i);h|=0;} return h.toString(); }

  function handlePinEnter() {
    if (simpleHash(pinInput) === pinHash) {
      setScreen("main"); setPinInput(""); setPinError("");
      persist({ lastActive: Date.now() });
    } else { setPinError("Incorrect PIN — try again"); setPinInput(""); }
  }

  function handlePinSetup() {
    if (pinSetup.length < 4) { setPinError("PIN must be at least 4 digits"); return; }
    setPinStep("confirm"); setPinError("");
  }

  function handlePinConfirm() {
    if (pinSetup !== pinConfirm) { setPinError("PINs don't match — try again"); setPinConfirm(""); return; }
    const hash = simpleHash(pinSetup);
    setPinHash(hash); setPinEnabled(true);
    persist({ pinEnabled: true, pinHash: hash, pinTimeout });
    setPinSetup(""); setPinConfirm(""); setPinError(""); setPinStep("enter");
    setShowPinSettings(false);
    showToast("PIN set! 🔒");
  }

  function disablePin() { setPinEnabled(false); setPinHash(""); persist({ pinEnabled:false, pinHash:"" }); showToast("PIN removed 🔓"); }

  const cat = CATEGORIES.find(c=>c.id===activeCategory);
 
  // ── PIN SCREEN ──
  if (screen === "pin") return (
    <div style={S.fullPage}>
      <div style={S.onboardCard}>
        <div style={S.logoMark}>🔒</div>
        <h1 style={S.appTitle}>Groundwork</h1>
        <p style={S.tagline}>Enter your PIN to continue</p>
        <input style={{ ...S.input, textAlign:"center", fontSize:"24px", letterSpacing:"8px" }}
          type="password" inputMode="numeric" maxLength={8} placeholder="••••"
          value={pinInput} onChange={e=>setPinInput(e.target.value)}
          onKeyDown={e=>e.key==="Enter"&&handlePinEnter()} />
        {pinError && <p style={S.fieldError}>{pinError}</p>}
        <button style={S.primaryBtn} onClick={handlePinEnter}>Unlock</button>
      </div>
    </div>
  );

  // ── ONBOARDING ──
  if (screen === "onboard") return (
    <div style={S.fullPage}>
      <div style={S.onboardCard}>
        <div style={{display:"flex", justifyContent:"center", marginBottom:"16px"}}>
  <img src="/logo192.png" style={{width:"52px", height:"52px", borderRadius:"12px"}} alt="Groundwork" />
        </div>
        <h1 style={S.appTitle}>Groundwork</h1>
        <p style={S.tagline}>Your first real budget. Built by you.</p>
        <div style={{ textAlign:"center", marginBottom:"20px" }}>
          <div style={S.avatarWrapLarge} onClick={()=>fileRef.current?.click()}>
            {avatar ? <img src={avatar} alt="You" style={S.avatarImgLarge} />
              : <div style={S.avatarPlaceholder}>
                  <span style={{fontSize:"36px"}}>📷</span>
                </div>}
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleAvatar} />
          <p style={{fontSize:"12px",color:P.muted,margin:"8px 0 0"}}>Photo can be added or changed anytime.</p>
        </div>
        <div style={S.fieldGroup }>
          <label style={S.label}>★ What should we call you?</label>
          <input style={{...S.input, borderColor:!name.trim()?P.accent:P.border}}
            placeholder="Your name or gamer tag 😄" value={name}
            onChange={e=>setName(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleOnboard()} />
          {!name.trim() && <p style={S.fieldError}>👆 Enter a name to continue</p>}
        </div>
        <div style={S.fieldGroup}>
          <label style={S.label}>★ Monthly take-home pay (after tax)</label>
          <div style={S.row}>
            <span style={S.dollar}>$</span>
            <input style={{...S.input,flex:1}} placeholder="0" type="number" value={income} onChange={e=>setIncome(e.target.value)} />
            <select style={S.select} value={incomeFreq} onChange={e=>setIncomeFreq(parseFloat(e.target.value))}>
              <option value={1}>/ month</option>
              <option value={4.33}>/ week ×4.33</option>
              <option value={2.17}>/ bi-week ×2.17</option>
            </select>
          </div>
          <p style={S.hint}>Enter $0 if you don't know yet — update anytime.</p>
        </div>
        <button style={{...S.primaryBtn,opacity:name.trim()?1:0.45,cursor:name.trim()?"pointer":"not-allowed"}}
          onClick={handleOnboard}>Let's Build My Budget →</button>
        <div style={{display:"flex",gap:"10px",marginTop:"10px"}}>
          <button style={S.onboardSecondaryBtn} onClick={()=>importRef.current?.click()}>
            📂<br/>Restore<br/>My Budget
          </button>
          <button style={{...S.onboardSecondaryBtn, borderColor:P.red, color:P.red}} onClick={()=>setShowHelp(true)}>
            ❓<br/>Help<br/>Me
          </button>
          <button style={S.onboardSecondaryBtn} onClick={()=>{ setShowPinSettings(true); setScreen("main"); }}>
            🔒<br/>Set<br/>My PIN
          </button>
        </div>
        <input ref={importRef} type="file" accept=".json" style={{display:"none"}} onChange={importBudget}/>
      </div>
    </div>
  );

  // ── MAIN APP ──
  return (
    <div style={S.appShell}>
      {showHelp && <HelpMe onClose={()=>setShowHelp(false)} />}

      {/* Floating Help Button */}
      <button style={S.helpFloat} onClick={()=>setShowHelp(true)}>❓</button>
      {/* Toast */}
      {toast && (
        <div style={{...S.toast, background: toast.type==="error" ? P.red : P.saveGreen}}>
          {toast.msg}
        </div>
      )}

      {/* Move to category modal */}
      {movingItem && (
        <div style={S.modalOverlay}>
          <div style={S.modalCard}>
            <h3 style={{margin:"0 0 4px",fontSize:"16px",color:P.slate}}>Move to Category</h3>
            <p style={{margin:"0 0 16px",fontSize:"13px",color:P.muted}}>Where does "{movingItem.name}" belong?</p>
            <div style={{display:"flex",flexDirection:"column",gap:"7px",maxHeight:"300px",overflowY:"auto"}}>
              {CATEGORIES.map(c => (
                <button key={c.id} style={{...S.itemRow,borderColor:P.border,background:P.white,padding:"10px 14px"}}
                  onClick={()=>moveItem(movingItem.id, c.id)}>
                  <span style={{fontSize:"14px",fontWeight:"700",color:P.slate}}>{c.icon} {c.label}</span>
                </button>
              ))}
            </div>
            <button style={{...S.backBtnBottom,marginTop:"12px"}} onClick={()=>setMovingItem(null)}>Cancel</button>
          </div>
        </div>
      )}

      {/* PIN Settings modal */}
      {showPinSettings && (
        <div style={S.modalOverlay}>
          <div style={S.modalCard}>
            <h3 style={{margin:"0 0 4px",fontSize:"16px",color:P.slate}}>🔒 PIN Protection</h3>
            {!pinEnabled ? (
              <>
                <p style={{margin:"0 0 16px",fontSize:"13px",color:P.muted}}>Set a PIN to protect your budget.</p>
                {pinStep === "enter" && (
                  <>
                    <label style={S.miniLabel}>Choose a PIN (4-8 digits)</label>
                    <input style={{...S.input,textAlign:"center",fontSize:"20px",letterSpacing:"6px",marginBottom:"10px"}}
                      type="password" inputMode="numeric" maxLength={8} placeholder="••••"
                      value={pinSetup} onChange={e=>setPinSetup(e.target.value)} />
                    <label style={S.miniLabel}>Lock after inactivity</label>
                    <select style={{...S.select,width:"100%",marginBottom:"10px"}}
                      value={pinTimeout} onChange={e=>setPinTimeout(parseInt(e.target.value))}>
                      {PIN_TIMEOUTS.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    {pinError && <p style={S.fieldError}>{pinError}</p>}
                    <button style={S.primaryBtn} onClick={handlePinSetup}>Continue →</button>
                  </>
                )}
                {pinStep === "confirm" && (
                  <>
                    <label style={S.miniLabel}>Confirm your PIN</label>
                    <input style={{...S.input,textAlign:"center",fontSize:"20px",letterSpacing:"6px",marginBottom:"10px"}}
                      type="password" inputMode="numeric" maxLength={8} placeholder="••••"
                      value={pinConfirm} onChange={e=>setPinConfirm(e.target.value)} />
                    {pinError && <p style={S.fieldError}>{pinError}</p>}
                    <button style={S.primaryBtn} onClick={handlePinConfirm}>Set PIN 🔒</button>
                  </>
                )}
              </>
            ) : (
              <>
                <p style={{margin:"0 0 8px",fontSize:"13px",color:P.green,fontWeight:"700"}}>✅ PIN is active</p>
                <label style={S.miniLabel}>Lock after inactivity</label>
                <select style={{...S.select,width:"100%",marginBottom:"14px"}}
                  value={pinTimeout} onChange={e=>{ setPinTimeout(parseInt(e.target.value)); persist({pinTimeout:parseInt(e.target.value)}); }}>
                  {PIN_TIMEOUTS.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
                <button style={{...S.primaryBtn,background:P.red,marginBottom:"8px"}} onClick={disablePin}>Remove PIN 🔓</button>
              </>
            )}
            <button style={S.backBtnBottom} onClick={()=>{ setShowPinSettings(false); setPinStep("enter"); setPinSetup(""); setPinConfirm(""); setPinError(""); }}>Close</button>
          </div>
        </div>
      )}

      {/* ── STICKY TOP ── */}
      <div style={S.stickyTop}>
        <header style={S.header}>
          <div style={S.headerLeft}>
            <div style={S.avatarMini} onClick={()=>setScreen("onboard")}>
              {avatar ? <img src={avatar} alt="You" style={{width:"100%",height:"100%",objectFit:"cover",borderRadius:"50%"}} />
                : <span style={{fontSize:"18px"}}>👤</span>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" style={{display:"none"}} onChange={handleAvatar} />
            <div>
              <div style={S.appTitleSmall}>Groundwork</div>
              <div style={S.heySmall}>Hey, {name} 👋</div>
            </div>
          </div>
          <div style={S.headerRight}>
            <button style={view==="categories" ? S.tabActive : S.tabMuted}
              onClick={()=>{ setView("sheet"); setActiveCategory(null); }}>
              {view==="sheet" ? (
                <span>📋 Viewing<br/>Budget</span>
              ) : (
                <span>📋 Go To<br/>Budget Sheet</span>
              )}
            </button>
            <button style={view==="sheet" ? S.tabActive : S.tabMuted}
              onClick={()=>setView("categories")}>
              {view==="categories" ? (
    <span>➕ Adding<br/>Items…</span>
  ) : (
    <span>➕ Add Item<br/>To Budget</span>
  )}
            </button>
            <button
              style={{...S.saveBtn, background: hasUnsaved ? P.saveGreen : "rgba(255,255,255,0.12)", color: hasUnsaved ? P.white : "rgba(255,255,255,0.3)", cursor: hasUnsaved ? "pointer" : "default", border: hasUnsaved ? `1px solid ${P.saveGreen}` : "1px solid rgba(255,255,255,0.1)"}}
              onClick={hasUnsaved ? handleSave : undefined}>
              💾 Save
            </button>
          </div>
        </header>

        {/* Banner */}
        <div style={{...S.banner, background: inGreen ? P.green : P.red}}>
          <div style={S.bannerItem}>
            <span style={S.bannerLabel}>Monthly Income</span>
            <div style={S.bannerValueRow}>
              <span style={S.bannerValue}>{fmt(monthlyIncome)}</span>
              <button style={S.bannerEditBtn} onClick={()=>setScreen("onboard")}>✎</button>
            </div>
          </div>
          <div style={S.bannerDiv}/>
          <div style={S.bannerItem}>
            <span style={S.bannerLabel}>Total Out</span>
            <span style={S.bannerValue}>{fmt(totalOut)}</span>
          </div>
          <div style={S.bannerDiv}/>
          <div style={S.bannerItem}>
            <span style={S.bannerLabel}>{inGreen?"✅ Surplus":"⚠️ Deficit"}</span>
            <span style={{...S.bannerValue,fontWeight:900}}>{fmt(Math.abs(delta))}</span>
          </div>
        </div>
      </div>

      {/* ── CONTENT ── */}
      <div style={S.content}>

        {/* ════ SHEET ════ */}
        {view==="sheet" && (
          <div>
            <div style={S.sheetToolbar}>
              <button style={S.ghostBtn} onClick={()=>{ setView("categories"); setActiveCategory(null); }}>✏️ Edit Expenses</button>
              <button style={S.ghostBtn} onClick={()=>setScreen("onboard")}>💰 Edit Income</button>
              <button style={S.ghostBtn} onClick={exportBudget}>📁 Export<br/>My Budget</button>
                      </div>

            {(lineItems.length===0&&savingsGoals.length===0&&debtItems.length===0) ? (
              <div style={S.emptyState}>
                <div style={{fontSize:"52px",marginBottom:"12px"}}>📋</div>
                <p style={{fontSize:"18px",fontWeight:700,color:P.slate,margin:"0 0 8px"}}>Your budget sheet is empty.</p>
                <p style={{fontSize:"14px",color:P.muted,margin:0}}>Tap <strong>Add Item to Budget</strong> above to get started.</p>
              </div>
            ) : (
              <>
                {ALL_CATS.map(cat => {
                  const catItems = lineItems.filter(i=>i.catId===cat.id);
                  if (catItems.length===0) return null;
                  const catTotal = catItems.reduce((s,i)=>s+(parseFloat(i.amount)||0)*i.freq,0);
                  const collapsed = collapsedCats[cat.id];
                  return (
                    <div key={cat.id} style={S.sheetSection}>
                      <button style={{...S.sheetCatHeader,borderLeft:`4px solid ${cat.color}`}}
                        onClick={()=>toggleCollapse(cat.id)}>
                        <span>{cat.icon} {cat.label}{cat.id==="misc"&&<span style={{fontSize:"11px",color:P.amber,marginLeft:"6px"}}>· tap ↗ to organize</span>}</span>
                        <div style={S.catHeaderRight}>
                          <span style={S.catTotal}>{fmt(catTotal)}/mo</span>
                          <span style={{fontSize:"12px",color:P.muted}}>{collapsed?"▶":"▼"}</span>
                        </div>
                      </button>
                      {!collapsed && catItems.map(item => (
                        <div key={item.id} style={S.sheetRow}>
                          <span style={S.sheetItemName}>{item.name}</span>
                          <div style={S.sheetItemRight}>
                            {editId===item.id
                              ? <input style={S.editInput} autoFocus value={editVal} type="number"
                                  onChange={e=>setEditVal(e.target.value)}
                                  onBlur={()=>{ updateItem(item.id,editVal); setEditId(null); }}
                                  onKeyDown={e=>{ if(e.key==="Enter"){updateItem(item.id,editVal);setEditId(null);} }} />
                              : <span style={S.sheetAmt} onClick={()=>{ setEditId(item.id); setEditVal(item.amount); }}>
                                  {fmt((parseFloat(item.amount)||0)*item.freq)} <span style={S.editPencil}>✎</span>
                                </span>
                            }
                            {cat.id==="misc" && (
                              <button style={S.moveBtn} onClick={()=>setMovingItem(item)} title="Move to category">↗</button>
                            )}
                            <button style={S.removeBtn} onClick={()=>removeItem(item.id)}>✕</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}

                {savingsGoals.length>0 && (
                  <div style={S.sheetSection}>
                    <button style={{...S.sheetCatHeader,borderLeft:`4px solid ${P.gold}`}} onClick={()=>toggleCollapse("__savings")}>
                      <span>🎯 Saving Goals</span>
                      <div style={S.catHeaderRight}>
                        <span style={S.catTotal}>{fmt(totalSavings)}/mo</span>
                        <span style={{fontSize:"12px",color:P.muted}}>{collapsedCats["__savings"]?"▶":"▼"}</span>
                      </div>
                    </button>
                    {!collapsedCats["__savings"] && savingsGoals.map(g=>(
                      <div key={g.id} style={S.sheetRow}>
                        <div style={{flex:1}}>
                          <div style={S.sheetItemName}>{g.name}</div>
                          {g.target>0&&<div style={{fontSize:"11px",color:P.muted}}>Target: {fmtFull(g.target)}{g.monthly>0&&` · ~${Math.ceil(g.target/g.monthly)} months`}</div>}
                        </div>
                        <div style={S.sheetItemRight}>
                          {editId===`sg_${g.id}`
                            ? <input style={S.editInput} autoFocus value={editVal} type="number"
                                onChange={e=>setEditVal(e.target.value)}
                                onBlur={()=>{ updateSavingsGoal(g.id,"monthly",editVal); setEditId(null); }}
                                onKeyDown={e=>{ if(e.key==="Enter"){updateSavingsGoal(g.id,"monthly",editVal);setEditId(null);} }} />
                            : <span style={S.sheetAmt} onClick={()=>{ setEditId(`sg_${g.id}`); setEditVal(g.monthly); }}>
                                {fmt(g.monthly)}/mo <span style={S.editPencil}>✎</span>
                              </span>
                          }
                          <button style={S.removeBtn} onClick={()=>removeSavingsGoal(g.id)}>✕</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {debtItems.length>0 && (
                  <div style={S.sheetSection}>
                    <button style={{...S.sheetCatHeader,borderLeft:`4px solid ${P.red}`}} onClick={()=>toggleCollapse("__debt")}>
                      <span>💳 Pay Off Debt</span>
                      <div style={S.catHeaderRight}>
                        <span style={S.catTotal}>{fmt(totalDebt)}/mo</span>
                        <span style={{fontSize:"12px",color:P.muted}}>{collapsedCats["__debt"]?"▶":"▼"}</span>
                      </div>
                    </button>
                    {!collapsedCats["__debt"] && debtItems.map(d=>(
                      <div key={d.id} style={S.sheetRow}>
                        <div style={{flex:1}}>
                          <div style={S.sheetItemName}>{d.name}</div>
                          {d.balance>0&&<div style={{fontSize:"11px",color:P.muted}}>Balance: {fmtFull(d.balance)}{d.monthly>0&&` · ~${Math.ceil(d.balance/d.monthly)} months`}</div>}
                        </div>
                        <div style={S.sheetItemRight}>
                          {editId===`dt_${d.id}`
                            ? <input style={S.editInput} autoFocus value={editVal} type="number"
                                onChange={e=>setEditVal(e.target.value)}
                                onBlur={()=>{ updateDebtItem(d.id,"monthly",editVal); setEditId(null); }}
                                onKeyDown={e=>{ if(e.key==="Enter"){updateDebtItem(d.id,"monthly",editVal);setEditId(null);} }} />
                            : <span style={{...S.sheetAmt,color:P.red}} onClick={()=>{ setEditId(`dt_${d.id}`); setEditVal(d.monthly); }}>
                                {fmt(d.monthly)}/mo <span style={S.editPencil}>✎</span>
                              </span>
                          }
                          <button style={S.removeBtn} onClick={()=>removeDebtItem(d.id)}>✕</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div style={S.grandTotal}>
                  <div style={S.grandRow}><span>Regular Expenses</span><span>{fmt(totalExpenses)}</span></div>
                  <div style={S.grandRow}><span>Saving Goals</span><span style={{color:P.gold}}>{fmt(totalSavings)}</span></div>
                  <div style={S.grandRow}><span>Debt Payments</span><span style={{color:P.red}}>{fmt(totalDebt)}</span></div>
                  <div style={{...S.grandRow,borderTop:"1px solid rgba(255,255,255,0.2)",paddingTop:"10px",marginTop:"4px"}}>
                    <span style={{fontWeight:900,fontSize:"16px"}}>Total Monthly Out</span>
                    <span style={{fontWeight:900,fontSize:"16px"}}>{fmt(totalOut)}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ════ CATEGORIES GRID ════ */}
        {view==="categories" && !activeCategory && (
          <div>
            <p style={{...S.sectionHint, textAlign:"center", fontSize:"14px"}}>Tap a Category to browse and add expenses.<br/>Set & Edit the amounts on the Budget Sheet.</p>
            <div style={S.catGrid}>
              {CATEGORIES.map(cat => {
                const count = lineItems.filter(i=>i.catId===cat.id).length;
                return (
                  <button key={cat.id} style={{...S.catCard,borderTop:`3px solid ${cat.color}`}}
                    onClick={()=>setActiveCategory(cat.id)}>
                    <span style={S.catIcon}>{cat.icon}</span>
                    <span style={S.catName}>{cat.label}</span>
                    {count>0&&<span style={{...S.catBadge,background:cat.color}}>{count}</span>}
                  </button>
                );
              })}
              <button style={{...S.catCard,borderTop:`3px solid ${P.gold}`}} onClick={()=>setActiveCategory("savings")}>
                <span style={S.catIcon}>🎯</span><span style={S.catName}>Saving Goals</span>
                {savingsGoals.length>0&&<span style={{...S.catBadge,background:P.gold}}>{savingsGoals.length}</span>}
              </button>
              <button style={{...S.catCard,borderTop:`3px solid ${P.red}`}} onClick={()=>setActiveCategory("debt")}>
                <span style={S.catIcon}>💳</span><span style={S.catName}>Pay Off Debt</span>
                {debtItems.length>0&&<span style={{...S.catBadge,background:P.red}}>{debtItems.length}</span>}
              </button>
              <button style={{...S.catCard,borderTop:`3px solid ${P.muted}`}} onClick={()=>setActiveCategory("misc")}>
                <span style={S.catIcon}>📦</span><span style={S.catName}>Miscellaneous</span>
                {lineItems.filter(i=>i.catId==="misc").length>0&&<span style={{...S.catBadge,background:P.muted}}>{lineItems.filter(i=>i.catId==="misc").length}</span>}
              </button>
            </div>

            <div style={S.customSection}>
              <h3 style={S.customTitle}>➕ Add a Custom Expense</h3>
              <input style={S.input} placeholder="Start typing an expense name…" value={customName}
                onChange={e=>setCustomName(e.target.value)} />
              <div style={S.row}>
                <span style={S.dollar}>$</span>
                <input style={{...S.input,flex:1}} placeholder="Amount" type="number" value={customAmount}
                  onChange={e=>setCustomAmount(e.target.value)} />
                <select style={S.select} value={customFreq} onChange={e=>setCustomFreq(parseFloat(e.target.value))}>
                  <option value={1}>/ month</option><option value={4.33}>/ week</option>
                  <option value={2.17}>/ bi-week</option><option value={1/12}>/ year</option>
                </select>
              </div>
              <button
                style={{...S.primaryBtn, opacity:customName.trim()&&customAmount?1:0.45, cursor:customName.trim()&&customAmount?"pointer":"not-allowed"}}
                onClick={()=>{
                  if(!customName.trim()||!customAmount) return;
                  addItem("misc",customName.trim(),customAmount,customFreq);
                  setCustomName(""); setCustomAmount(""); setCustomFreq(1);
                  showToast(`"${customName.trim()}" added to Miscellaneous! ✅`);
                }}>Add Custom Expense to Budget</button>
              <p style={{fontSize:"13px",color:P.bold,margin:"2px 0 0",textAlign:"center",fontStyle:"italic"}}>Will be Added to Miscellaneous<br/>Move to a real category later from the Budget Sheet</p>
            </div>
          </div>
        )}

        {/* ════ CATEGORY ITEMS ════ */}
        {view==="categories" && activeCategory && cat && (
          <div>
            <button style={S.backBtn} onClick={goBack}>← Back to Categories</button>
            <div style={S.catTitleBar}>{cat.icon} {cat.label}</div>
            <p style={{...S.sectionHint, fontSize:"15px", textAlign:"center"}}>Tap any item to add it. Tap again to remove it.<br/>Set & Edit the amounts on the Budget Sheet.</p>
            <div style={S.itemList}>
              {cat.items.map(item=>{
                const added=isAdded(cat.id,item.name);
                return (
                  <button key={item.name}
                    style={{...S.itemRow,background:added?P.greenBg:P.white,borderColor:added?P.green:P.border}}
                    onClick={()=>{
                      if(added){const ex=lineItems.find(i=>i.catId===cat.id&&i.name===item.name);if(ex)removeItem(ex.id);}
                      else{ addItem(cat.id,item.name,item.suggested,1); showToast(`"${item.name}" added! ✅`); }
                    }}>
                    <div style={S.itemLeft}>
                      <span style={S.itemName}>{item.name}</span>
                      <span style={S.itemSuggested}>Suggested: {fmt(item.suggested)}/mo</span>
                    </div>
                    <span style={{...S.itemCheck,color:added?P.green:P.muted}}>{added?"✓ Added":"+ Add"}</span>
                  </button>
                );
              })}
            </div>
            <div style={S.customSection}>
              <h3 style={S.customTitle}>➕ Custom {cat.label} Expense</h3>
              <input style={S.input} placeholder="Start typing an expense name…" value={customName}
                onChange={e=>setCustomName(e.target.value)} />
              <div style={S.row}>
                <span style={S.dollar}>$</span>
                <input style={{...S.input,flex:1}} placeholder="Amount" type="number" value={customAmount}
                  onChange={e=>setCustomAmount(e.target.value)} />
                <select style={S.select} value={customFreq} onChange={e=>setCustomFreq(parseFloat(e.target.value))}>
                  <option value={1}>/ month</option><option value={4.33}>/ week</option>
                  <option value={2.17}>/ bi-week</option><option value={1/12}>/ year</option>
                </select>
              </div>
              <button
                style={{...S.primaryBtn,opacity:customName.trim()&&customAmount?1:0.45,cursor:customName.trim()&&customAmount?"pointer":"not-allowed"}}
                onClick={()=>{
                  if(!customName.trim()||!customAmount) return;
                  addItem(cat.id,customName.trim(),customAmount,customFreq);
                  showToast(`"${customName.trim()}" added! ✅`);
                  setCustomName(""); setCustomAmount(""); setCustomFreq(1);
                }}>Add Custom Expense to Budget</button>
            </div>
            <button style={S.backBtnBottom} onClick={goBack}>← Back to Categories</button>
          </div>
        )}

        {/* ════ MISC CATEGORY ════ */}
        {view==="categories" && activeCategory==="misc" && (
          <div>
            <button style={S.backBtn} onClick={goBack}>← Back to Categories</button>
            <div style={S.catTitleBar}>📦 Miscellaneous</div>
            <p style={{...S.sectionHint, textAlign:"center"}}>Expenses you haven't categorized yet.<br/>Tap ↗ on the Budget Sheet to move them to a category.</p>
            <div style={S.customSection}>
              <h3 style={S.customTitle}>➕ Add Miscellaneous Expense</h3>
              <input style={S.input} placeholder="Start typing an expense name…" value={customName}
                onChange={e=>setCustomName(e.target.value)} />
              <div style={S.row}>
                <span style={S.dollar}>$</span>
                <input style={{...S.input,flex:1}} placeholder="Amount" type="number" value={customAmount}
                  onChange={e=>setCustomAmount(e.target.value)} />
                <select style={S.select} value={customFreq} onChange={e=>setCustomFreq(parseFloat(e.target.value))}>
                  <option value={1}>/ month</option><option value={4.33}>/ week</option>
                  <option value={2.17}>/ bi-week</option><option value={1/12}>/ year</option>
                </select>
              </div>
              <button
                style={{...S.primaryBtn,opacity:customName.trim()&&customAmount?1:0.45,cursor:customName.trim()&&customAmount?"pointer":"not-allowed"}}
                onClick={()=>{
                  if(!customName.trim()||!customAmount) return;
                  addItem("misc",customName.trim(),customAmount,customFreq);
                  showToast(`"${customName.trim()}" added! ✅`);
                  setCustomName(""); setCustomAmount(""); setCustomFreq(1);
                }}>Add to Miscellaneous</button>
            </div>
            <button style={S.backBtnBottom} onClick={goBack}>← Back to Categories</button>
          </div>
        )}

        {/* ════ SAVING GOALS ════ */}
        {view==="categories" && activeCategory==="savings" && (
          <div>
            <button style={S.backBtn} onClick={goBack}>← Back to Categories</button>
            <div style={S.catTitleBar}>🎯 Saving Goals</div>
            <p style={{...S.sectionHint, textAlign:"center"}}>Set a goal and how much to set aside each month toward it.<br/>Set & Edit the amounts on the Budget Sheet.</p>
            <div style={S.itemList}>
              {SAVINGS_GOALS.map(goal=>{
                const added=hasSavingsGoal(goal.name);
                return (
                  <button key={goal.name}
                    style={{...S.itemRow,background:added?P.greenBg:P.white,borderColor:added?P.green:P.border}}
                    onClick={()=>{
                      if(added){const ex=savingsGoals.find(g=>g.name===goal.name);if(ex)removeSavingsGoal(ex.id);}
                      else{ addSavingsGoal(goal.name,0,goal.suggested); showToast(`"${goal.name}" added! ✅`); }
                    }}>
                    <div style={S.itemLeft}>
                      <span style={S.itemName}>{goal.icon} {goal.name}</span>
                      <span style={S.itemSuggested}>Suggested: {fmt(goal.suggested)}/mo</span>
                    </div>
                    <span style={{...S.itemCheck,color:added?P.green:P.muted}}>{added?"✓ Added":"+ Add"}</span>
                  </button>
                );
              })}
            </div>
            {savingsGoals.length>0&&(
              <div style={{marginBottom:"16px"}}>
                <h3 style={{...S.customTitle,marginBottom:"12px"}}>📝 Edit Your Saving Goals</h3>
                {savingsGoals.map(g=>(
                  <div key={g.id} style={S.goalCard}>
                    <div style={S.goalCardHeader}>
                      <span style={S.goalName}>{g.name}</span>
                      <button style={S.removeBtn} onClick={()=>removeSavingsGoal(g.id)}>✕</button>
                    </div>
                    <div style={S.goalFields}>
                      <div style={S.goalField}>
                        <label style={S.miniLabel}>🎯 Target Amount</label>
                        <div style={S.row}><span style={S.dollar}>$</span>
                          <input style={{...S.input,flex:1}} type="number" placeholder="Total goal"
                            value={g.target||""} onChange={e=>updateSavingsGoal(g.id,"target",e.target.value)} /></div>
                      </div>
                      <div style={S.goalField}>
                        <label style={S.miniLabel}>📅 Monthly Set-Aside</label>
                        <div style={S.row}><span style={S.dollar}>$</span>
                          <input style={{...S.input,flex:1}} type="number" placeholder="Per month"
                            value={g.monthly||""} onChange={e=>updateSavingsGoal(g.id,"monthly",e.target.value)} /></div>
                      </div>
                    </div>
                    {g.target>0&&g.monthly>0&&(
                      <div style={S.goalEta}>⏱ At {fmt(g.monthly)}/mo you'll hit your goal in ~{Math.ceil(g.target/g.monthly)} months</div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div style={S.customSection}>
              <h3 style={S.customTitle}>➕ Custom Saving Goal</h3>
              <input style={S.input} placeholder="Goal name (e.g. Gaming PC)" value={customGoalName}
                onChange={e=>setCustomGoalName(e.target.value)} />
              <div style={S.row}><span style={S.dollar}>$</span>
                <input style={{...S.input,flex:1}} placeholder="Target amount" type="number"
                  value={customGoalTarget} onChange={e=>setCustomGoalTarget(e.target.value)} /></div>
              <div style={S.row}><span style={S.dollar}>$</span>
                <input style={{...S.input,flex:1}} placeholder="Monthly set-aside" type="number"
                  value={customGoalMonthly} onChange={e=>setCustomGoalMonthly(e.target.value)} />
                <span style={{fontSize:"13px",color:P.muted,whiteSpace:"nowrap"}}>/ month</span></div>
              <button style={{...S.primaryBtn,opacity:customGoalName.trim()?1:0.45,cursor:customGoalName.trim()?"pointer":"not-allowed"}}
                onClick={()=>{
                  if(!customGoalName.trim()) return;
                  addSavingsGoal(customGoalName.trim(),customGoalTarget,customGoalMonthly);
                  showToast(`"${customGoalName.trim()}" goal added! ✅`);
                  setCustomGoalName(""); setCustomGoalTarget(""); setCustomGoalMonthly("");
                }}>Add Custom Saving Goal</button>
            </div>
            <button style={S.backBtnBottom} onClick={goBack}>← Back to Categories</button>
          </div>
        )}

        {/* ════ PAY OFF DEBT ════ */}
        {view==="categories" && activeCategory==="debt" && (
          <div>
            <button style={S.backBtn} onClick={goBack}>← Back to Categories</button>
            <div style={S.catTitleBar}>💳 Pay Off Debt</div>
            <p style={{...S.sectionHint,fontSize:"14px",textAlign:"center"}}>Add debts you're working to pay off.<br/>Enter the total balance owed below, and your monthly payment.</p>
            <div style={S.itemList}>
              {DEBT_ITEMS.map(debt=>{
                const added=hasDebtItem(debt.name);
                return (
                  <button key={debt.name}
                    style={{...S.itemRow,background:added?P.redBg:P.white,borderColor:added?P.red:P.border}}
                    onClick={()=>{
                      if(added){const ex=debtItems.find(d=>d.name===debt.name);if(ex)removeDebtItem(ex.id);}
                      else{ addDebtItem(debt.name,0,0); showToast(`"${debt.name}" added! ✅`); }
                    }}>
                    <div style={S.itemLeft}>
                      <span style={S.itemName}>{debt.icon} {debt.name}</span>
                      <span style={S.itemSuggested}>Tap to add, then enter balance & payment below</span>
                    </div>
                    <span style={{...S.itemCheck,color:added?P.red:P.muted}}>{added?"✓ Added":"+ Add"}</span>
                  </button>
                );
              })}
            </div>
            {debtItems.length>0&&(
              <div style={{marginBottom:"16px"}}>
                <h3 style={{...S.customTitle,marginBottom:"12px"}}>📝 Edit Your Debts</h3>
                {debtItems.map(d=>(
                  <div key={d.id} style={{...S.goalCard,borderLeft:`3px solid ${P.red}`}}>
                    <div style={S.goalCardHeader}>
                      <span style={S.goalName}>{d.name}</span>
                      <button style={S.removeBtn} onClick={()=>removeDebtItem(d.id)}>✕</button>
                    </div>
                    <div style={S.goalFields}>
                      <div style={S.goalField}>
                        <label style={S.miniLabel}>💰 Total Balance</label>
                        <div style={S.row}><span style={S.dollar}>$</span>
                          <input style={{...S.input,flex:1}} type="number" placeholder="What you owe"
                            value={d.balance||""} onChange={e=>updateDebtItem(d.id,"balance",e.target.value)} /></div>
                      </div>
                      <div style={S.goalField}>
                        <label style={S.miniLabel}>📅 Monthly Payment</label>
                        <div style={S.row}><span style={S.dollar}>$</span>
                          <input style={{...S.input,flex:1}} type="number" placeholder="Per month"
                            value={d.monthly||""} onChange={e=>updateDebtItem(d.id,"monthly",e.target.value)} /></div>
                      </div>
                    </div>
                    {d.balance>0&&d.monthly>0&&(
                      <div style={{...S.goalEta,color:P.red}}>🏁 At {fmt(d.monthly)}/mo you'll pay this off in ~{Math.ceil(d.balance/d.monthly)} months</div>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div style={S.customSection}>
              <h3 style={S.customTitle}>➕ Custom Debt</h3>
              <input style={S.input} placeholder="Debt name (e.g. Buy Now Pay Later)" value={customDebtName}
                onChange={e=>setCustomDebtName(e.target.value)} />
              <div style={S.row}><span style={S.dollar}>$</span>
                <input style={{...S.input,flex:1}} placeholder="Total balance owed" type="number"
                  value={customDebtBalance} onChange={e=>setCustomDebtBalance(e.target.value)} /></div>
              <div style={S.row}><span style={S.dollar}>$</span>
                <input style={{...S.input,flex:1}} placeholder="Monthly payment" type="number"
                  value={customDebtMonthly} onChange={e=>setCustomDebtMonthly(e.target.value)} />
                <span style={{fontSize:"13px",color:P.muted,whiteSpace:"nowrap"}}>/ month</span></div>
              <button style={{...S.primaryBtn,opacity:customDebtName.trim()?1:0.45,cursor:customDebtName.trim()?"pointer":"not-allowed"}}
                onClick={()=>{
                  if(!customDebtName.trim()) return;
                  addDebtItem(customDebtName.trim(),customDebtBalance,customDebtMonthly);
                  showToast(`"${customDebtName.trim()}" added! ✅`);
                  setCustomDebtName(""); setCustomDebtBalance(""); setCustomDebtMonthly("");
                }}>Add Custom Debt</button>
            </div>
            <button style={S.backBtnBottom} onClick={goBack}>← Back to Categories</button>
          </div>
        )}
      </div>

     </div>
  );
}

const S = {
  fullPage: { minHeight:"100vh", background:`linear-gradient(135deg,#F6F1E9 0%,#EDE5D4 100%)`, display:"flex", alignItems:"center", justifyContent:"center", padding:"20px", fontFamily:"'Georgia','Times New Roman',serif" },
  onboardCard: { background:"#FFFFFF", borderRadius:"16px", padding:"40px 36px", maxWidth:"440px", width:"100%", boxShadow:"0 8px 40px rgba(44,50,64,0.13)"},
  logoMark: { width:"52px", height:"52px", background:"#252D3D", borderRadius:"12px", display:"flex", alignItems:"center", justifyContent:"center", color:"#C8773A", fontSize:"26px", fontWeight:"900", marginBottom:"16px"},
  appTitle: { fontSize:"34px", fontWeight:"700", color:"#252D3D", margin:"0 0 4px", letterSpacing:"-1px",  textAlign:"center"},
  tagline: { color:"#8A90A8", fontSize:"15px", margin:"0 0 24px", fontStyle:"italic", textAlign:"center" },
  avatarWrapLarge: { width:"180px", height:"180px", borderRadius:"50%", border:`2.5px dashed #D8CFBE`, cursor:"pointer", display:"inline-flex", flexDirection:"column", alignItems:"center", justifyContent:"center", overflow:"hidden", background:"#EDE5D4" },
  avatarImgLarge: { width:"100%", height:"100%", objectFit:"cover" },
  avatarPlaceholder: { display:"flex", flexDirection:"column", alignItems:"center", gap:"4px" },
  fieldGroup: { marginBottom:"18px" },
  label: { display:"block", fontSize:"12px", fontWeight:"700", color:"#252D3D", marginBottom:"7px", textTransform:"uppercase", letterSpacing:"0.6px", textAlign:"center" },
  input: { width:"100%", padding:"11px 13px", border:`1.5px solid #D8CFBE`, borderRadius:"8px", fontSize:"15px", color:"#252D3D", background:"#F6F1E9", outline:"none", boxSizing:"border-box", fontFamily:"inherit", textAlign:"center" },
  row: { display:"flex", alignItems:"center", gap:"7px" },
  dollar: { fontSize:"17px", color:"#8A90A8", fontWeight:"700", flexShrink:0 },
  select: { padding:"11px 9px", border:`1.5px solid #D8CFBE`, borderRadius:"8px", fontSize:"15px", color:"#252D3D", background:"#F6F1E9", outline:"none", cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap", flexShrink:0 },
  hint: { fontSize:"13px", color:"#8A90A8", margin:"5px 0 0", fontStyle:"italic", textAlign:"center" },
  fieldError: { fontSize:"12px", color:"#C8773A", margin:"5px 0 0", fontStyle:"italic" },
  primaryBtn: { width:"100%", padding:"14px", background:"#252D3D", color:"#F6F1E9", border:"none", borderRadius:"10px", fontSize:"15px", fontWeight:"700", cursor:"pointer", fontFamily:"inherit" },
  appShell: { minHeight:"100vh", background:"#F6F1E9", fontFamily:"'Georgia','Times New Roman',serif", display:"flex", flexDirection:"column", maxWidth:"640px", margin:"0 auto", position:"relative" },
  stickyTop: { position:"sticky", top:0, zIndex:20 },
  header: { background:"#252D3D", padding:"10px 12px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:"8px", flexWrap:"wrap" },
  headerLeft: { display:"flex", alignItems:"center", gap:"10px", width:"100%" },
  avatarMini: { width:"40px", height:"40px", borderRadius:"50%", border:`2px solid #C8773A`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden", background:"#3A4460", flexShrink:0 },
  appTitleSmall: { color:"#F6F1E9", fontSize:"15px", fontWeight:"700", letterSpacing:"-0.3px" },
  heySmall: { color:"#F0A070", fontSize:"12px" },
  headerRight: { display:"flex", gap:"5px", flexWrap:"wrap", flex:1, justifyContent:"flex-end", width:"100%" },
  tabMuted: { padding:"2px 5px", background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:"7px", color:"rgba(255,255,255,0.35)", fontSize:"13px", cursor:"default", fontFamily:"inherit", fontStyle:"italic", flex:1, textAlign:"center", lineHeight:1.4 },
  tabActive: { padding:"2px 5px", background:"#C8773A", border:`1px solid #C8773A`, borderRadius:"7px", color:"#FFFFFF", fontSize:"13px", cursor:"pointer", fontWeight:"700", fontFamily:"inherit", flex:1, textAlign:"center", lineHeight:1.4 },
  saveBtn: { padding:"6px 10px", borderRadius:"7px", fontSize:"15px", fontWeight:"700", fontFamily:"inherit", transition:"all 0.3s", flex:1, textAlign:"center" },
  banner: { display:"flex", justifyContent:"space-around", padding:"13px 10px", transition:"background 0.4s" },
  bannerItem: { display:"flex", flexDirection:"column", alignItems:"center", gap:"2px" },
  bannerLabel: { fontSize:"10px", color:"rgba(255,255,255,0.75)", textTransform:"uppercase", letterSpacing:"0.7px", fontWeight:"600" },
  bannerValue: { fontSize:"17px", color:"#FFFFFF", fontWeight:"700" },
  bannerValueRow: { display:"flex", alignItems:"center", gap:"6px" },
  bannerEditBtn: { background:"rgba(255,255,255,0.2)", border:"none", color:"#FFFFFF", borderRadius:"6px", fontSize:"20px", cursor:"pointer", padding:"2px 8px", lineHeight:1 },
  bannerDiv: { width:"1px", background:"rgba(255,255,255,0.2)", alignSelf:"stretch" },
  content: { flex:1, padding:"14px", overflowY:"auto" },
  sheetToolbar: { display:"flex", gap:"6px", marginBottom:"14px", flexWrap:"wrap" },
  ghostBtn: { flex:1, padding:"8px 10px", background:"#FFFFFF", border:`1.5px solid #D8CFBE`, borderRadius:"8px", color:"#3A4460", fontSize:"12px", cursor:"pointer", fontFamily:"inherit", fontWeight:"600", minWidth:"60px" },
  emptyState: { textAlign:"center", padding:"60px 20px" },
  sheetSection: { background:"#FFFFFF", borderRadius:"10px", marginBottom:"10px", overflow:"hidden", border:`1px solid #D8CFBE` },
  sheetCatHeader: { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"12px 14px 12px 12px", background:"#EDE5D4", fontSize:"14px", fontWeight:"700", color:"#252D3D", width:"100%", border:"none", cursor:"pointer", textAlign:"left", fontFamily:"inherit" },
  catHeaderRight: { display:"flex", alignItems:"center", gap:"10px" },
  catTotal: { fontSize:"13px", fontWeight:"800", color:"#3A4460" },
  sheetRow: { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"10px 14px", borderTop:`1px solid #D8CFBE` },
  sheetItemName: { fontSize:"14px", color:"#252D3D", flex:1 },
  sheetItemRight: { display:"flex", alignItems:"center", gap:"8px" },
  sheetAmt: { fontSize:"14px", fontWeight:"700", color:"#3A4460", cursor:"pointer", border:`1px solid #D8CFBE`, borderRadius:"6px", padding:"3px 8px", background:"#F6F1E9" },
  editPencil: { fontSize:"12px", color:"#8A90A8" },
  editInput: { width:"80px", padding:"4px 8px", border:`1.5px solid #C8773A`, borderRadius:"6px", fontSize:"14px", fontWeight:"700", color:"#252D3D", background:"#F6F1E9", outline:"none", fontFamily:"inherit" },
  moveBtn: { background:"#FEF3E2", border:`1px solid #E8A020`, color:"#E8A020", borderRadius:"5px", fontSize:"13px", cursor:"pointer", padding:"2px 6px", fontWeight:"700" },
  removeBtn: { background:"none", border:"none", color:"#8A90A8", cursor:"pointer", fontSize:"14px", padding:"2px 4px", lineHeight:1 },
  grandTotal: { background:"#252D3D", borderRadius:"10px", padding:"16px", color:"#F6F1E9", marginTop:"4px" },
  grandRow: { display:"flex", justifyContent:"space-between", fontSize:"14px", marginBottom:"6px" },
  catTitleBar: { fontSize:"20px", fontWeight:"900", color:"#252D3D", textAlign:"center", padding:"10px 0 14px", letterSpacing:"-0.3px" },
  sectionHint: { fontSize:"13px", color:"#8A90A8", marginBottom:"12px", fontStyle:"italic" },
  catGrid: { display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"9px", marginBottom:"22px" },
  catCard: { background:"#FFFFFF", border:`1px solid #D8CFBE`, borderRadius:"10px", padding:"14px 8px", display:"flex", flexDirection:"column", alignItems:"center", gap:"5px", cursor:"pointer", position:"relative", fontFamily:"inherit" },
  catIcon: { fontSize:"22px" },
  catName: { fontSize:"11px", fontWeight:"700", color:"#252D3D", textAlign:"center", lineHeight:1.2 },
  catBadge: { position:"absolute", top:"5px", right:"5px", minWidth:"17px", height:"17px", borderRadius:"9px", color:"#FFFFFF", fontSize:"10px", fontWeight:"800", display:"flex", alignItems:"center", justifyContent:"center", padding:"0 3px" },
  customSection: { background:"#FFFFFF", border:`1px solid #D8CFBE`, borderRadius:"12px", padding:"18px", display:"flex", flexDirection:"column", gap:"10px", marginBottom:"16px" },
  customTitle: { margin:0, fontSize:"15px", fontWeight:"700", color:"#252D3D" },
  backBtn: { background:"none", border:"none", color:"#C8773A", fontSize:"14px", fontWeight:"700", cursor:"pointer", padding:"0 0 12px 0", fontFamily:"inherit" },
  backBtnBottom: { background:"none", border:`2px solid #C8773A`, borderRadius:"8px", color:"#C8773A", fontSize:"13px", fontWeight:"700", cursor:"pointer", padding:"10px", fontFamily:"inherit", textAlign:"center", width:"100%", boxSizing:"border-box" },
  onboardSecondaryBtn: { flex:1, padding:"10px 8px", background:"none", border:`1.5px solid #D8CFBE`, borderRadius:"10px", color:"#3A4460", fontSize:"13px", fontWeight:"700", cursor:"pointer", fontFamily:"inherit", textAlign:"center", lineHeight:1.4 },
  itemList: { display:"flex", flexDirection:"column", gap:"7px", marginBottom:"20px" },
  itemRow: { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"13px", border:"1.5px solid", borderRadius:"10px", cursor:"pointer", textAlign:"left", fontFamily:"inherit" },
  itemLeft: { display:"flex", flexDirection:"column", gap:"3px" },
  itemName: { fontSize:"14px", fontWeight:"700", color:"#252D3D" },
  itemSuggested: { fontSize:"11px", color:"#8A90A8" },
  itemCheck: { fontSize:"13px", fontWeight:"700", whiteSpace:"nowrap", flexShrink:0 },
  goalCard: { background:"#FFFFFF", border:`1px solid #D8CFBE`, borderLeft:`3px solid #C8A43A`, borderRadius:"10px", padding:"14px", marginBottom:"10px" },
  goalCardHeader: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"12px" },
  goalName: { fontSize:"14px", fontWeight:"700", color:"#252D3D" },
  goalFields: { display:"flex", gap:"10px", flexWrap:"wrap" },
  goalField: { flex:1, minWidth:"140px", display:"flex", flexDirection:"column", gap:"5px" },
  miniLabel: { fontSize:"11px", fontWeight:"700", color:"#8A90A8", textTransform:"uppercase", letterSpacing:"0.4px" },
  goalEta: { marginTop:"10px", fontSize:"12px", color:"#2E7D57", background:"#E8F5EE", borderRadius:"6px", padding:"7px 10px", fontWeight:"600" },
  toast: { position:"fixed", bottom:"16px", left:"50%", transform:"translateX(-50%)", color:"#FFFFFF", padding:"10px 20px", borderRadius:"20px", fontSize:"14px", fontWeight:"700", zIndex:100, boxShadow:"0 4px 20px rgba(0,0,0,0.2)", fontFamily:"'Georgia',serif", whiteSpace:"nowrap" },
  modalOverlay: { position:"fixed", inset:0, background:"rgba(37,45,61,0.7)", zIndex:50, display:"flex", alignItems:"center", justifyContent:"center", padding:"20px" },
  modalCard: { background:"#FFFFFF", borderRadius:"16px", padding:"24px", width:"100%", maxWidth:"380px", boxShadow:"0 8px 40px rgba(0,0,0,0.2)", fontFamily:"'Georgia',serif" },
  helpFloat: { position:"absolute", top:"14px", right:"14px", width:"36px", height:"36px", borderRadius:"50%", background:P.accentLight, border:"3px solid" , color:"#f3064d", fontSize:"24px", cursor:"pointer", zIndex:30, display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 3px 12px rgba(176,58,58,0.4)", fontFamily:"inherit" },
  
  
};
